<script>
import axios from "axios";
import { toast, variants } from "@/helpers/toaster";

const STATUS_BADGE = {
  draft: "secondary",
  created: "info",
  committed: "success",
  printed: "success",
  cancelled: "dark",
  error: "danger",
};

const STATUS_LABEL = {
  draft: "Skica",
  created: "Kreirano",
  committed: "Štampano",
  printed: "Štampano",
  cancelled: "Otkazano",
  error: "Greška",
};

const DEFAULT_FILTERS = () => ({
  statuses: ["created"],
  from: null,
  to: null,
  q: "",
});

export default {
  name: "CityExpressShipmentList",
  props: {
    active: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      showExportModal: false,
      exporting: false,
      exportMonth: "",
      exportStatus: null,
      exportStatusLoading: false,
      exportStatusSequence: 0,
      exportPollTimer: null,
      requestingExport: false,
      exportError: "",
      refreshingStatuses: false,
      refreshingRowId: null,
      busyRows: {}, // id -> 'print' | 'cancel' | 'discard'
      shipments: [],
      meta: { current_page: 1, last_page: 1, per_page: 50, total: 0 },
      filters: DEFAULT_FILTERS(),
      searchDebounceTimer: null,
      loadSequence: 0,
      selectedIds: [], // ids of shipments ticked for the bulk-print action
      printingBatch: false,
      pickupBusy: false, // "Kraj dana" pickup request in flight
      statusOptions: [
        { value: "created", text: "Kreirano" },
        { value: "printed", text: "Štampano" },
        { value: "cancelled", text: "Otkazano" },
        { value: "error", text: "Greška" },
      ],
      page: 1,
      perPage: 50,
      perPageOptions: [25, 50, 100, 200],
      showErrorModal: false,
      errorModalShipment: null,
      showDetailModal: false,
      detailShipment: null,
      // Inline edit of a CREATED shipment before its first print.
      editMode: false,
      savingEdit: false,
      editForm: null,
    };
  },
  computed: {
    currentExportMonth() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    },
    exportMonthValid() {
      return /^\d{4}-(0[1-9]|1[0-2])$/.test(this.exportMonth) &&
        this.exportMonth <= this.currentExportMonth;
    },
    fields() {
      return [
        { key: "select", label: "", class: "text-center ce-col-select" },
        { key: "created_at", label: "Datum", formatter: this.formatDateTime },
        { key: "shipment_id", label: "CE ID" },
        { key: "status", label: "Status" },
        { key: "cosignee_name", label: "Primalac" },
        { key: "reference", label: "Reference" },
        { key: "otpremnice_count", label: "Otpr.", class: "text-center" },
        { key: "number_of_packages", label: "Pak.", class: "text-right" },
        { key: "net_value", label: "Osnovica (RSD)", class: "text-right" },
        { key: "vat_value", label: "PDV (RSD)", class: "text-right" },
        { key: "value", label: "Ukupno (RSD)", class: "text-right" },
        { key: "actions", label: "Akcije", class: "text-right" },
      ];
    },
    statusEventFields() {
      return [
        { key: "event_at", label: "Vreme" },
        { key: "code", label: "Kod" },
        { key: "message", label: "Opis" },
      ];
    },
    statusButtonLabel() {
      const n = this.filters.statuses.length;
      if (n === 0) return "Status: svi";
      if (n === this.statusOptions.length) return "Status: svi";
      if (n === 1) {
        return "Status: " + this.statusLabel(this.filters.statuses[0]);
      }
      return `Status: ${n} izabranih`;
    },
    activeFilterChips() {
      const chips = [];
      if (this.filters.statuses.length === 0) {
        chips.push({ key: "statuses", label: "Status: svi" });
      } else {
        for (const s of this.filters.statuses) {
          chips.push({
            key: `status:${s}`,
            label: `Status: ${this.statusLabel(s)}`,
          });
        }
      }
      if (this.filters.from) {
        chips.push({ key: "from", label: `Od: ${this.filters.from}` });
      }
      if (this.filters.to) {
        chips.push({ key: "to", label: `Do: ${this.filters.to}` });
      }
      if (this.filters.q && this.filters.q.trim()) {
        chips.push({
          key: "q",
          label: `Pretraga: \u201c${this.filters.q.trim()}\u201d`,
        });
      }
      return chips;
    },
    // Rows that can be bulk-printed; CE performs its internal commit on the
    // first print and reprints labels for already printed rows.
    selectableShipments() {
      return this.shipments.filter((s) => this.canSelect(s));
    },
    allSelectableSelected() {
      return (
        this.selectableShipments.length > 0 &&
        this.selectableShipments.every((s) => this.selectedIds.includes(s.id))
      );
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    // Feature A: of the ticked rows, which are eligible for a "Kraj dana"
    // pickup (printed only; committed is retained for legacy rows).
    pickupEligibleIds() {
      const sel = new Set(this.selectedIds);
      return this.shipments
        .filter(
          (s) =>
            sel.has(s.id) &&
            (s.status === "committed" || s.status === "printed") &&
            !s.pickup_requested_at,
        )
        .map((s) => s.id);
    },
    // Feature C: the shipment shown in the detail modal can only be edited
    // while it is still in the CREATED state (before first print locks it).
    detailEditable() {
      return !!this.detailShipment && this.detailShipment.status === "created";
    },
  },
  async mounted() {
    await this.load();
  },
  beforeDestroy() {
    this.stopExportPolling();
    this.exportStatusSequence++;
  },
  watch: {
    active(isActive, wasActive) {
      if (isActive && wasActive === false) this.load();
      if (!isActive) this.stopExportPolling();
      else if (this.showExportModal) this.loadExportStatus();
    },
  },
  methods: {
    formatTaxValue(record, key) {
      return record.net_value_status === "verified" && record[key] != null
        ? this.formatMoney(record[key])
        : "Nerazrešeno";
    },
    openExport() {
      if (!this.exportMonth) {
        const today = new Date();
        const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
        this.exportMonth = `${lastDay.getFullYear()}-${String(
          lastDay.getMonth() + 1,
        ).padStart(2, "0")}`;
      }
      this.exportError = "";
      this.showExportModal = true;
      this.loadExportStatus();
    },
    stopExportPolling() {
      clearTimeout(this.exportPollTimer);
      this.exportPollTimer = null;
    },
    closeExport() {
      this.stopExportPolling();
      this.exportStatusSequence++;
      this.exportStatusLoading = false;
    },
    async loadExportStatus() {
      this.stopExportPolling();
      const sequence = ++this.exportStatusSequence;
      const month = this.exportMonth;
      if (this.exportStatus && this.exportStatus.month !== month) this.exportStatus = null;
      this.exportError = "";
      if (!this.exportMonthValid) {
        this.exportStatus = null;
        this.exportStatusLoading = false;
        return;
      }
      this.exportStatusLoading = true;
      try {
        const { data } = await axios.get(
          "/super-admin/city-express/shipments/export/status",
          { params: { month } },
        );
        if (sequence !== this.exportStatusSequence) return;
        this.exportStatus = data.data;
        if (data.data.requested && this.showExportModal && this.active) {
          this.exportPollTimer = setTimeout(() => this.loadExportStatus(), 15000);
        }
      } catch (error) {
        if (sequence !== this.exportStatusSequence) return;
        this.exportError = "Status izvoza nije dostupan. Pokušajte ponovo.";
      } finally {
        if (sequence === this.exportStatusSequence) this.exportStatusLoading = false;
      }
    },
    async requestExport() {
      if (this.requestingExport || !this.exportMonthValid) return;
      this.requestingExport = true;
      this.exportError = "";
      const month = this.exportMonth;
      try {
        await axios.post(
          "/super-admin/city-express/shipments/export/request",
          { month },
        );
        if (this.showExportModal && this.exportMonth === month) await this.loadExportStatus();
      } catch (error) {
        this.exportError = "Zahtev za pripremu izvoza nije uspeo. Pokušajte ponovo.";
      } finally {
        this.requestingExport = false;
      }
    },
    async exportShipments() {
      if (this.exporting || !this.exportMonthValid || !this.exportStatus || !this.exportStatus.ready) return;
      this.exporting = true;
      this.exportError = "";
      const month = this.exportMonth;
      try {
        const { data } = await axios.get(
          "/super-admin/city-express/shipments/export",
          {
            params: { month },
            responseType: "blob",
          },
        );
        const url = URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.download = `cityexpress-obracun-${month}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.showExportModal = false;
      } catch (error) {
        let message = "Izvoz obračuna nije uspeo. Pokušajte ponovo.";
        const response = error.response && error.response.data;
        if (response instanceof Blob) {
          try {
            const details = JSON.parse(await response.text());
            message = details.message || message;
          } catch (parseError) {
            message = "Izvoz obračuna nije uspeo. Pokušajte ponovo.";
          }
        } else if (response && response.message) {
          message = response.message;
        }
        this.exportError = message;
      } finally {
        this.exporting = false;
      }
    },
    badgeVariant(s) {
      return STATUS_BADGE[s] || "secondary";
    },
    statusLabel(s) {
      return STATUS_LABEL[s] || s;
    },
    async load() {
      const sequence = ++this.loadSequence;
      this.loading = true;
      try {
        const params = {
          page: this.page,
          per_page: this.perPage,
        };
        if (this.filters.statuses && this.filters.statuses.length) {
          params.status = this.filters.statuses;
        }
        if (this.filters.from) params.from = this.filters.from;
        if (this.filters.to) params.to = this.filters.to;
        if (this.filters.q && this.filters.q.trim())
          params.q = this.filters.q.trim();

        const { data } = await axios.get(
          "/super-admin/city-express/shipments",
          { params, paramsSerializer: this.paramsSerializer },
        );
        if (sequence !== this.loadSequence) return;
        this.shipments = data.data || [];
        this.meta = data.meta || this.meta;
        // Drop any selected ids that are no longer present in the new page.
        const present = new Set(this.shipments.map((s) => s.id));
        this.selectedIds = this.selectedIds.filter((id) => present.has(id));
      } finally {
        if (sequence === this.loadSequence) this.loading = false;
      }
    },
    paramsSerializer(params) {
      const parts = [];
      for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined || value === "") continue;
        if (Array.isArray(value)) {
          for (const v of value) {
            parts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(v)}`);
          }
        } else {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      }
      return parts.join("&");
    },
    applyFilters() {
      this.page = 1;
      this.load();
    },
    // Use the value the checkbox-group emits instead of reading filters.statuses
    // afterwards — the v-model update can land a tick later, which made the
    // list filter one click behind ("weird" all/subset results).
    onStatusesChange(values) {
      this.filters.statuses = Array.isArray(values) ? values : [];
      this.applyFilters();
    },
    onSearchInput() {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => this.applyFilters(), 350);
    },
    resetFilters() {
      this.filters = DEFAULT_FILTERS();
      this.applyFilters();
    },
    removeChip(chip) {
      if (chip.key === "from") this.filters.from = null;
      else if (chip.key === "to") this.filters.to = null;
      else if (chip.key === "q") this.filters.q = "";
      else if (chip.key === "statuses") {
        this.filters.statuses = DEFAULT_FILTERS().statuses;
      } else if (chip.key.startsWith("status:")) {
        const s = chip.key.slice("status:".length);
        this.filters.statuses = this.filters.statuses.filter((x) => x !== s);
      }
      this.applyFilters();
    },
    onPageChange(p) {
      this.page = p;
      this.load();
    },
    onPerPageChange(n) {
      this.perPage = Number(n) || 50;
      this.page = 1;
      this.load();
    },
    isSelected(row) {
      return this.selectedIds.includes(row.id);
    },
    canSelect(row) {
      return ["created", "committed", "printed"].includes(row.status);
    },
    toggleSelect(row) {
      if (!this.canSelect(row)) return;
      const i = this.selectedIds.indexOf(row.id);
      if (i >= 0) this.selectedIds.splice(i, 1);
      else if (this.selectedIds.length < 100) this.selectedIds.push(row.id);
      else
        toast(
          "City Express dozvoljava najviše 100 pošiljki po štampi.",
          variants.WARNING,
        );
    },
    toggleSelectAll() {
      if (this.allSelectableSelected) {
        this.selectedIds = [];
      } else {
        this.selectedIds = this.selectableShipments
          .slice(0, 100)
          .map((s) => s.id);
        if (this.selectableShipments.length > 100) {
          toast(
            "Izabrano je prvih 100 pošiljki, što je CE maksimum po štampi.",
            variants.WARNING,
          );
        }
      }
    },
    async printSelected() {
      if (this.printingBatch || this.selectedIds.length === 0) return;
      this.printingBatch = true;
      try {
        const {
          data,
        } = await axios.post(
          "/super-admin/city-express/shipments/print-batch",
          { shipment_ids: this.selectedIds },
        );
        const updated = (data.data && data.data.shipments) || [];
        for (const s of updated) this.replaceRow(s);
        if (data.data && data.data.label_base64) {
          this.downloadPdf(
            data.data.label_base64,
            `cityexpress-batch-${Date.now()}.pdf`,
          );
        }
        const printed = (data.data && data.data.printed) || updated.length;
        const failed = (data.data && data.data.failed) || 0;
        if (failed > 0) {
          toast(
            `Štampano ${printed} pošiljki, ${failed} sa greškom.`,
            variants.WARNING,
          );
        } else {
          toast(`Štampano ${printed} pošiljki.`, variants.SUCCESS);
        }
        this.selectedIds = [];
      } catch (e) {
        toast(
          (e.response && e.response.data && e.response.data.message) ||
            "Greška pri štampi izabranih pošiljki.",
          variants.DANGER,
        );
      } finally {
        this.printingBatch = false;
      }
    },
    canPrint(row) {
      return ["created", "committed", "printed"].includes(row.status);
    },
    canCancel(row) {
      return row.status === "created";
    },
    canDiscard(row) {
      return (
        ["committed", "printed"].includes(row.status) &&
        !row.pickup_requested_at &&
        !row.pickup_list_number
      );
    },
    async print(row) {
      if (!this.canPrint(row)) return;
      this.$set(this.busyRows, row.id, "print");
      try {
        const { data } = await axios.post(
          `/super-admin/city-express/shipments/${row.id}/print`,
        );
        const updated = data.data && data.data.shipment;
        if (updated) this.replaceRow(updated);
        if (data.data && data.data.label_base64) {
          this.downloadPdf(
            data.data.label_base64,
            `cityexpress-${row.shipment_id}.pdf`,
          );
        } else {
          toast("Pošiljka štampana (PDF nije prosleđen).", variants.WARNING);
        }
      } catch (err) {
        this.handleActionError(err, row);
      } finally {
        this.$delete(this.busyRows, row.id);
      }
    },
    async cancel(row) {
      if (!this.canCancel(row)) return;
      const ok = await this.confirm(
        `Otkazati i obrisati pošiljku ${row.shipment_id ||
          "#" +
            row.id}? Biće otkazana kod City Express-a i uklonjena iz ove liste.`,
      );
      if (!ok) return;
      this.$set(this.busyRows, row.id, "cancel");
      try {
        const { data } = await axios.post(
          `/super-admin/city-express/shipments/${row.id}/cancel`,
        );
        const deleted = data.data;
        if (deleted && deleted.deleted) {
          this.shipments = this.shipments.filter((s) => s.id !== deleted.id);
          this.selectedIds = this.selectedIds.filter((id) => id !== deleted.id);
          this.meta.total = Math.max(0, Number(this.meta.total || 0) - 1);
        }
        toast("Pošiljka otkazana u CE i obrisana.", variants.SUCCESS);
      } catch (err) {
        this.handleActionError(err, row);
      } finally {
        this.$delete(this.busyRows, row.id);
      }
    },
    async discard(row) {
      if (!this.canDiscard(row)) return;
      const ok = await this.confirmDiscard(
        `Ukloniti pošiljku ${row.shipment_id || "#" + row.id} iz liste? ` +
          "Stara nalepnica više ne sme da se koristi. Otpremnice će ponovo biti dostupne za pripremu.",
      );
      if (!ok) return;
      this.$set(this.busyRows, row.id, "discard");
      try {
        const { data } = await axios.post(
          `/super-admin/city-express/shipments/${row.id}/discard`,
        );
        const discarded = data.data;
        if (discarded && discarded.discarded) {
          this.shipments = this.shipments.filter(
            (shipment) => shipment.id !== discarded.id,
          );
          this.selectedIds = this.selectedIds.filter(
            (id) => id !== discarded.id,
          );
          this.meta.total = Math.max(0, Number(this.meta.total || 0) - 1);
        }
        toast(
          "Pošiljka uklonjena. Otpremnice su ponovo dostupne za pripremu.",
          variants.SUCCESS,
        );
      } catch (err) {
        this.handleActionError(err, row);
      } finally {
        this.$delete(this.busyRows, row.id);
      }
    },
    handleActionError(err, row) {
      const data = err.response && err.response.data;
      const msg =
        (data && data.message) || err.message || "Greška u komunikaciji sa CE.";
      if (data && data.shipment) {
        this.replaceRow(data.shipment);
      }
      toast(`Pošiljka #${row.id}: ${msg}`, variants.DANGER);
    },
    replaceRow(updated) {
      const idx = this.shipments.findIndex((s) => s.id === updated.id);
      if (idx >= 0) this.$set(this.shipments, idx, updated);
    },
    confirm(message) {
      if (this.$bvModal && this.$bvModal.msgBoxConfirm) {
        return this.$bvModal.msgBoxConfirm(message, {
          title: "Potvrda",
          okTitle: "Da, otkaži",
          cancelTitle: "Nazad",
          okVariant: "danger",
          centered: true,
        });
      }
      return Promise.resolve(window.confirm(message));
    },
    confirmDiscard(message) {
      if (this.$bvModal && this.$bvModal.msgBoxConfirm) {
        return this.$bvModal.msgBoxConfirm(message, {
          title: "Uklanjanje pošiljke",
          okTitle: "Ukloni",
          cancelTitle: "Nazad",
          okVariant: "danger",
          centered: true,
        });
      }
      return Promise.resolve(window.confirm(message));
    },
    downloadPdf(base64, filename) {
      try {
        const cleaned = String(base64).replace(
          /^data:application\/pdf;base64,/,
          "",
        );
        const bin = atob(cleaned);
        const buf = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
        const blob = new Blob([buf], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        // Open in a new tab so the user can preview/print, matching the
        // pre-existing City Express UX.
        const win = window.open(url, "_blank");
        if (!win) {
          // Pop-up blocked — fall back to download.
          const a = document.createElement("a");
          a.href = url;
          a.download = filename || "cityexpress-label.pdf";
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } catch (e) {
        toast("Ne mogu da otvorim PDF.", variants.DANGER);
      }
    },
    openError(row) {
      this.errorModalShipment = row;
      this.showErrorModal = true;
    },
    openDetail(row) {
      this.detailShipment = row;
      this.editMode = false;
      this.editForm = null;
      this.showDetailModal = true;
    },
    // Human-readable editability contract shown in the detail modal so the
    // operator understands when a shipment can still be changed.
    editabilityHint(status) {
      switch (status) {
        case "draft":
          return "Skica — može se menjati (još nije poslata na City Express).";
        case "created":
          return "Kreirano — može se menjati do prve štampe.";
        case "committed":
          return "Štampano — zaključano; izmene nisu moguće.";
        case "printed":
          return "Štampano — zaključano; izmene nisu moguće.";
        case "cancelled":
          return "Otkazano.";
        case "error":
          return "Greška pri kreiranju.";
        default:
          return "";
      }
    },
    startEdit() {
      const s = this.detailShipment;
      if (!s) return;
      this.editForm = {
        reference: s.reference || "",
        payment_by: s.payment_by || "sender",
        number_of_packages: s.number_of_packages || 1,
        is_pallet: !!s.is_pallet,
        cod_amount:
          s.cod_amount === null || s.cod_amount === undefined
            ? null
            : Number(s.cod_amount),
        note: s.note || "",
        cosignee: {
          name: s.cosignee_name || "",
          street_and_number: s.cosignee_street_and_number || "",
          zipcode: s.cosignee_zipcode || "",
          city: s.cosignee_city || "",
          country_code: s.cosignee_country_code || "RS",
          contact_name: s.cosignee_contact_name || "",
          phone: s.cosignee_phone || "",
          email: s.cosignee_email || "",
        },
      };
      this.editMode = true;
    },
    cancelEdit() {
      this.editMode = false;
      this.editForm = null;
    },
    async saveEdit() {
      const s = this.detailShipment;
      if (!s || this.savingEdit || !this.editForm) return;
      this.savingEdit = true;
      try {
        const c = this.editForm.cosignee;
        const payload = {
          cosignee: {
            name: c.name,
            street_and_number: c.street_and_number,
            zipcode: c.zipcode,
            city: c.city,
            country_code: (c.country_code || "RS").toUpperCase(),
            contact_name: c.contact_name || null,
            phone: c.phone || null,
            email: c.email || null,
          },
          reference: this.editForm.reference || null,
          payment_by: this.editForm.payment_by || "sender",
          number_of_packages: Number(this.editForm.number_of_packages) || 1,
          is_pallet: !!this.editForm.is_pallet,
          cod_amount:
            this.editForm.cod_amount === "" ||
            this.editForm.cod_amount === null ||
            this.editForm.cod_amount === undefined
              ? null
              : Number(this.editForm.cod_amount),
          note: this.editForm.note || null,
        };
        const { data } = await axios.put(
          `/super-admin/city-express/shipments/${s.id}`,
          payload,
        );
        const updated = data.data;
        if (updated) {
          this.replaceRow(updated);
          this.detailShipment = updated;
        }
        this.editMode = false;
        this.editForm = null;
        toast("Pošiljka izmenjena.", variants.SUCCESS);
      } catch (e) {
        const d = e.response && e.response.data;
        if (d && d.shipment) {
          this.replaceRow(d.shipment);
          this.detailShipment = d.shipment;
        }
        toast(
          (d && d.message) || "Greška pri izmeni pošiljke.",
          variants.DANGER,
        );
      } finally {
        this.savingEdit = false;
      }
    },
    // Feature A: "Kraj dana" — request a driver pickup manifest for the
    // eligible (committed/printed) selected shipments and open the returned
    // signed-handover PDF(s).
    async requestPickup() {
      const ids = this.pickupEligibleIds;
      if (this.pickupBusy || ids.length === 0) return;
      let ok = true;
      if (this.$bvModal && this.$bvModal.msgBoxConfirm) {
        ok = await this.$bvModal.msgBoxConfirm(
          `Zatražiti preuzimanje (kraj dana) za ${ids.length} pošiljki? City Express generiše prijemnicu koju vozač potpisuje.`,
          {
            title: "Kraj dana — preuzimanje",
            okTitle: "Zatraži preuzimanje",
            cancelTitle: "Nazad",
            okVariant: "primary",
            centered: true,
          },
        );
      }
      if (!ok) return;
      this.pickupBusy = true;
      try {
        const {
          data,
        } = await axios.post(
          "/super-admin/city-express/shipments/request-pickup",
          { shipment_ids: ids },
        );
        const updated = (data.data && data.data.shipments) || [];
        for (const s of updated) this.replaceRow(s);
        const docs = (data.data && data.data.documents) || [];
        if (docs.length) {
          docs.forEach((doc, i) =>
            this.downloadPdf(
              doc,
              `cityexpress-preuzimanje-${Date.now()}-${i + 1}.pdf`,
            ),
          );
        } else {
          toast(
            "Preuzimanje zatraženo (CE nije prosledio PDF prijemnicu).",
            variants.WARNING,
          );
        }
        const requested = (data.data && data.data.requested) || updated.length;
        const failed = (data.data && data.data.failed) || 0;
        if (failed > 0) {
          toast(
            `Preuzimanje zatraženo za ${requested} pošiljki, ${failed} preskočeno.`,
            variants.WARNING,
          );
        } else {
          toast(
            `Preuzimanje zatraženo za ${requested} pošiljki.`,
            variants.SUCCESS,
          );
        }
        this.selectedIds = [];
      } catch (e) {
        toast(
          (e.response && e.response.data && e.response.data.message) ||
            "Greška pri zahtevu za preuzimanje.",
          variants.DANGER,
        );
      } finally {
        this.pickupBusy = false;
      }
    },
    /**
     * Bulk: ask the backend to re-pull /shipmentbyid for every shipment
     * whose status can still change. Reloads the list afterwards so the
     * (possibly updated) statuses + freshly stored status_events show up.
     */
    async refreshStatuses() {
      if (this.refreshingStatuses) return;
      this.refreshingStatuses = true;
      try {
        const { data } = await axios.post(
          "/super-admin/city-express/shipments/refresh-statuses",
        );
        const s = data.data || {};
        const refreshed = s.refreshed || 0;
        const failed = s.failed || 0;
        if (failed > 0) {
          toast(
            `Osveženo ${refreshed} pošiljki, ${failed} sa greškom.`,
            variants.WARNING,
          );
        } else if (refreshed === 0) {
          toast("Nema pošiljki za osvežavanje.", variants.INFO);
        } else {
          toast(`Osveženo ${refreshed} pošiljki.`, variants.SUCCESS);
        }
        await this.load();
      } catch (e) {
        toast(
          (e.response && e.response.data && e.response.data.message) ||
            "Greška pri osvežavanju statusa.",
          variants.DANGER,
        );
      } finally {
        this.refreshingStatuses = false;
      }
    },
    /**
     * Refresh a single shipment's status from inside the detail modal.
     * The returned row replaces the cached `detailShipment` so the timeline
     * table re-renders in place.
     */
    async refreshOneStatus(row) {
      if (!row || this.refreshingRowId === row.id) return;
      this.refreshingRowId = row.id;
      try {
        const { data } = await axios.post(
          `/super-admin/city-express/shipments/${row.id}/refresh-status`,
        );
        const updated = data.data;
        if (updated) {
          this.replaceRow(updated);
          this.detailShipment = updated;
        }
        toast("Status osvežen.", variants.SUCCESS);
      } catch (e) {
        toast(
          (e.response && e.response.data && e.response.data.message) ||
            "Greška pri osvežavanju statusa.",
          variants.DANGER,
        );
      } finally {
        this.refreshingRowId = null;
      }
    },
    formatDateTime(v) {
      if (!v) return "";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      return d.toLocaleString("sr-RS");
    },
    formatMoney(v) {
      if (v === null || v === undefined || v === "") return "";
      return Number(v).toLocaleString("sr-RS", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<template>
  <div>
    <div class="ce-toolbar mb-2">
      <div class="ce-toolbar__left">
        <b-input-group class="ce-search">
          <b-input-group-prepend is-text>
            <span aria-hidden="true">🔍</span>
          </b-input-group-prepend>
          <b-form-input
            v-model="filters.q"
            placeholder="Pretraga: referenca, primalac, CE ID…"
            @input="onSearchInput()"
            @keyup.enter="applyFilters()"
          />
          <b-input-group-append v-if="filters.q">
            <b-button
              variant="outline-secondary"
              @click="
                filters.q = '';
                applyFilters();
              "
            >
              ×
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <div class="ce-dates d-flex align-items-end" style="gap: 0.5rem">
          <div>
            <label class="mb-1">Od</label>
            <b-form-datepicker
              v-model="filters.from"
              locale="sr"
              :start-weekday="1"
              :date-format-options="{
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }"
              placeholder="Od"
              today-button
              reset-button
              close-button
              label-today-button="Danas"
              label-reset-button="Poništi"
              label-close-button="Zatvori"
              style="min-width: 180px"
              @input="applyFilters()"
            />
          </div>
          <div>
            <label class="mb-1">Do</label>
            <b-form-datepicker
              v-model="filters.to"
              locale="sr"
              :start-weekday="1"
              :date-format-options="{
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }"
              placeholder="Do"
              today-button
              reset-button
              close-button
              label-today-button="Danas"
              label-reset-button="Poništi"
              label-close-button="Zatvori"
              style="min-width: 180px"
              @input="applyFilters()"
            />
          </div>
        </div>

        <b-dropdown
          variant="outline-secondary"
          :text="statusButtonLabel"
          right
          no-flip
        >
          <b-dropdown-form style="min-width: 200px">
            <b-form-checkbox-group
              :checked="filters.statuses"
              :options="statusOptions"
              stacked
              @change="onStatusesChange"
            />
          </b-dropdown-form>
        </b-dropdown>

        <b-button
          v-if="activeFilterChips.length"
          size="sm"
          variant="link"
          class="px-1"
          @click="resetFilters()"
        >
          Resetuj
        </b-button>
      </div>

      <div class="ce-toolbar__right">
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="exporting"
          @click="openExport()"
        >
          <i class="fas fa-file-download mr-1" aria-hidden="true"></i>
          Izvoz obračuna
        </b-button>
        <b-button
          size="sm"
          variant="success"
          :disabled="printingBatch || selectedCount === 0"
          @click="printSelected()"
        >
          <b-spinner v-if="printingBatch" small class="mr-1" />
          Štampaj izabrane ({{ selectedCount }})
        </b-button>
        <b-button
          size="sm"
          variant="warning"
          :disabled="pickupBusy || pickupEligibleIds.length === 0"
          @click="requestPickup()"
          title="Zatvori dan i zatraži preuzimanje kod City Express-a"
        >
          <b-spinner v-if="pickupBusy" small class="mr-1" />
          Kraj dana ({{ pickupEligibleIds.length }})
        </b-button>
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="refreshingStatuses"
          @click="refreshStatuses()"
        >
          <b-spinner v-if="refreshingStatuses" small class="mr-1" />
          Osveži statuse
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="load()">
          Osveži
        </b-button>
      </div>
    </div>

    <div v-if="activeFilterChips.length" class="ce-chips mb-3">
      <b-badge
        v-for="chip in activeFilterChips"
        :key="chip.key"
        variant="light"
        class="ce-chip"
        @click="removeChip(chip)"
      >
        {{ chip.label }} <span class="ce-chip__x">×</span>
      </b-badge>
    </div>

    <b-modal
      v-model="showExportModal"
      title="CE mesečni obračun"
      :no-close-on-backdrop="exporting"
      :no-close-on-esc="exporting"
      :hide-header-close="exporting"
      @hidden="closeExport()"
    >
      <b-alert :show="Boolean(exportError)" variant="danger">
        {{ exportError }}
      </b-alert>
      <b-form-group label="Mesec" label-for="ce-export-month">
        <input
          id="ce-export-month"
          v-model="exportMonth"
          class="form-control"
          type="month"
          :max="currentExportMonth"
          :disabled="exporting || requestingExport"
          @change="loadExportStatus()"
        />
      </b-form-group>
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div role="status" aria-live="polite">
          <span v-if="exportStatusLoading && !exportStatus">Provera statusa...</span>
          <template v-else-if="exportStatus">
            <div v-if="exportStatus.ready">
              Poslednji izvoz: {{ formatDateTime(exportStatus.generated_at) }}
            </div>
            <div v-else>Izvoz još nije pripremljen.</div>
            <div v-if="exportStatus.requested" class="text-muted">Priprema zatražena.</div>
          </template>
        </div>
        <b-button
          size="sm"
          variant="outline-secondary"
          title="Proveri status izvoza"
          aria-label="Proveri status izvoza"
          :disabled="exportStatusLoading || !exportMonthValid"
          @click="loadExportStatus()"
        >
          <i class="fas fa-sync-alt" aria-hidden="true"></i>
        </b-button>
      </div>
      <template #modal-footer>
        <b-button
          variant="secondary"
          :disabled="exporting"
          @click="showExportModal = false"
        >
          Zatvori
        </b-button>
        <b-button
          variant="outline-primary"
          :disabled="requestingExport || exporting || !exportMonthValid || !exportStatus || exportStatus.requested"
          @click="requestExport()"
        >
          <b-spinner v-if="requestingExport" small class="mr-1" />
          <i v-else class="fas fa-sync-alt mr-1" aria-hidden="true"></i>
          {{ exportStatus && exportStatus.ready ? "Osveži datoteku" : "Pripremi izvoz" }}
        </b-button>
        <b-button
          variant="primary"
          :disabled="exporting || !exportMonthValid || !exportStatus || exportStatus.month !== exportMonth || !exportStatus.ready"
          @click="exportShipments()"
        >
          <b-spinner v-if="exporting" small class="mr-1" />
          <i v-else class="fas fa-file-download mr-1" aria-hidden="true"></i>
          Preuzmi CSV
        </b-button>
      </template>
    </b-modal>

    <b-table
      :items="shipments"
      :fields="fields"
      :busy="loading"
      small
      striped
      responsive
      hover
    >
      <template #head(select)>
        <b-form-checkbox
          :checked="allSelectableSelected"
          :disabled="selectableShipments.length === 0"
          @change="toggleSelectAll()"
        />
      </template>
      <template #cell(select)="row">
        <b-form-checkbox
          :checked="isSelected(row.item)"
          :disabled="!canSelect(row.item)"
          @change="toggleSelect(row.item)"
        />
      </template>

      <template #cell(otpremnice_count)="row">
        <b-badge
          v-if="(row.item.otpremnice || []).length > 1"
          variant="primary"
          title="Pošiljka objedinjuje više otpremnica"
        >
          {{ row.item.otpremnice.length }}
        </b-badge>
        <span v-else class="text-muted">
          {{ (row.item.otpremnice || []).length || "—" }}
        </span>
      </template>

      <template #cell(status)="row">
        <b-badge :variant="badgeVariant(row.item.status)">
          {{ statusLabel(row.item.status) }}
        </b-badge>
        <b-badge
          v-if="row.item.pickup_requested_at"
          variant="success"
          class="ml-1"
          :title="
            row.item.pickup_list_number
              ? 'Lista preuzimanja: ' + row.item.pickup_list_number
              : 'Kraj dana je završen'
          "
        >
          Kraj dana
        </b-badge>
        <b-button
          v-if="row.item.status === 'error' && row.item.last_error"
          size="sm"
          variant="link"
          class="p-0 ml-1"
          @click="openError(row.item)"
        >
          ?
        </b-button>
      </template>

      <template #cell(value)="row">
        {{ formatMoney(row.item.value) }}
      </template>

      <template #cell(vat_value)="row">
        {{ formatTaxValue(row.item, "vat_value") }}
      </template>

      <template #cell(net_value)="row">
        {{ formatTaxValue(row.item, "net_value") }}
      </template>

      <template #cell(cosignee_name)="row">
        <div>
          {{ row.item.cosignee_name }}
          <b-badge v-if="row.item.is_pallet" variant="warning" class="ml-1">
            Paleta
          </b-badge>
        </div>
        <small class="text-muted">
          {{ row.item.cosignee_street_and_number }},
          {{ row.item.cosignee_zipcode }} {{ row.item.cosignee_city }}
        </small>
      </template>

      <template #cell(actions)="row">
        <b-button-group size="sm">
          <b-button
            variant="outline-secondary"
            @click="openDetail(row.item)"
            title="Detalji"
          >
            i
          </b-button>
          <b-button
            v-if="canPrint(row.item)"
            variant="success"
            :disabled="!!busyRows[row.item.id]"
            @click="print(row.item)"
          >
            <span v-if="busyRows[row.item.id] === 'print'">…</span>
            <span v-else>Štampaj</span>
          </b-button>
          <b-button
            v-if="canCancel(row.item)"
            variant="outline-danger"
            :disabled="!!busyRows[row.item.id]"
            @click="cancel(row.item)"
          >
            <span v-if="busyRows[row.item.id] === 'cancel'">…</span>
            <span v-else>Otkaži</span>
          </b-button>
          <b-button
            v-if="canDiscard(row.item)"
            variant="outline-danger"
            :disabled="!!busyRows[row.item.id]"
            @click="discard(row.item)"
          >
            <span v-if="busyRows[row.item.id] === 'discard'">…</span>
            <span v-else>Ukloni</span>
          </b-button>
        </b-button-group>
      </template>

      <template #empty>
        <div class="text-center text-muted py-3">
          Nema pošiljki za prikaz.
        </div>
      </template>
      <template #table-busy>
        <div class="text-center py-3"><b-spinner small /> Učitavanje…</div>
      </template>
    </b-table>

    <div
      class="ce-pager d-flex justify-content-between align-items-center mt-2"
    >
      <small class="text-muted">
        Ukupno: {{ meta.total }} · Strana {{ meta.current_page }}/{{
          meta.last_page
        }}
      </small>
      <div class="d-flex align-items-center" style="gap: 0.5rem">
        <label class="mb-0 small text-muted">Po strani</label>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          size="sm"
          style="width: 90px"
          @change="onPerPageChange"
        />
        <b-pagination
          v-if="meta.last_page > 1"
          :value="meta.current_page"
          :total-rows="meta.total"
          :per-page="meta.per_page"
          size="sm"
          class="mb-0"
          @change="onPageChange"
        />
      </div>
    </div>

    <b-modal
      v-model="showErrorModal"
      title="Greška pošiljke"
      ok-only
      ok-title="Zatvori"
    >
      <div v-if="errorModalShipment">
        <p><strong>ID:</strong> #{{ errorModalShipment.id }}</p>
        <p>
          <strong>CE ID:</strong> {{ errorModalShipment.shipment_id || "—" }}
        </p>
        <p><strong>Poruka:</strong></p>
        <pre
          class="bg-light p-2"
          style="white-space: pre-wrap; word-break: break-word"
          >{{ errorModalShipment.last_error }}</pre
        >
      </div>
    </b-modal>

    <b-modal
      v-model="showDetailModal"
      title="Detalji pošiljke"
      size="lg"
      ok-only
      ok-title="Zatvori"
    >
      <div v-if="detailShipment">
        <b-alert
          show
          :variant="detailEditable ? 'info' : 'secondary'"
          class="py-2 px-3 mb-3 d-flex align-items-center"
        >
          <span>{{ editabilityHint(detailShipment.status) }}</span>
          <b-button
            v-if="detailEditable && !editMode"
            size="sm"
            variant="outline-primary"
            class="ml-auto"
            @click="startEdit()"
          >
            Izmeni
          </b-button>
        </b-alert>

        <!-- EDIT FORM (feature C): only for CREATED shipments, via CE
             /updateshipment. Locked once committed. -->
        <b-form v-if="editMode && editForm" @submit.prevent="saveEdit()">
          <div class="row">
            <div class="col-md-6">
              <h6 class="text-muted">Primalac</h6>
              <b-form-group label="Naziv">
                <b-form-input v-model="editForm.cosignee.name" maxlength="35" />
              </b-form-group>
              <b-form-group label="Adresa">
                <b-form-input
                  v-model="editForm.cosignee.street_and_number"
                  maxlength="70"
                />
              </b-form-group>
              <div class="row">
                <div class="col-4">
                  <b-form-group label="Pošt. broj">
                    <b-form-input
                      v-model="editForm.cosignee.zipcode"
                      maxlength="10"
                    />
                  </b-form-group>
                </div>
                <div class="col-5">
                  <b-form-group label="Grad">
                    <b-form-input
                      v-model="editForm.cosignee.city"
                      maxlength="35"
                    />
                  </b-form-group>
                </div>
                <div class="col-3">
                  <b-form-group label="Država">
                    <b-form-input
                      v-model="editForm.cosignee.country_code"
                      maxlength="2"
                    />
                  </b-form-group>
                </div>
              </div>
              <b-form-group label="Kontakt osoba">
                <b-form-input v-model="editForm.cosignee.contact_name" />
              </b-form-group>
              <div class="row">
                <div class="col-6">
                  <b-form-group label="Telefon">
                    <b-form-input
                      v-model="editForm.cosignee.phone"
                      maxlength="30"
                    />
                  </b-form-group>
                </div>
                <div class="col-6">
                  <b-form-group label="Email">
                    <b-form-input
                      v-model="editForm.cosignee.email"
                      type="email"
                    />
                  </b-form-group>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <h6 class="text-muted">Pošiljka</h6>
              <b-form-group label="Referenca">
                <b-form-input v-model="editForm.reference" maxlength="105" />
              </b-form-group>
              <div class="row">
                <div class="col-6">
                  <b-form-group
                    :label="editForm.is_pallet ? 'Broj paleta' : 'Broj paketa'"
                  >
                    <b-form-input
                      v-model.number="editForm.number_of_packages"
                      type="number"
                      min="1"
                      max="999"
                    />
                  </b-form-group>
                </div>
                <div class="col-6">
                  <b-form-group label="Otkup (COD)">
                    <b-form-input
                      v-model.number="editForm.cod_amount"
                      type="number"
                      min="0"
                      step="0.01"
                    />
                  </b-form-group>
                </div>
              </div>
              <b-form-checkbox v-model="editForm.is_pallet" class="mb-2">
                Paleta
              </b-form-checkbox>
              <b-form-checkbox
                v-model="editForm.payment_by"
                value="recipient"
                unchecked-value="sender"
                class="mb-2"
              >
                Primalac plaća poštarinu
              </b-form-checkbox>
              <b-form-group label="Napomena">
                <b-form-textarea
                  v-model="editForm.note"
                  rows="2"
                  max-rows="4"
                  maxlength="255"
                />
              </b-form-group>
            </div>
          </div>
          <div class="d-flex justify-content-end" style="gap: 0.5rem">
            <b-button
              variant="outline-secondary"
              :disabled="savingEdit"
              @click="cancelEdit()"
            >
              Odustani
            </b-button>
            <b-button variant="primary" type="submit" :disabled="savingEdit">
              <b-spinner v-if="savingEdit" small class="mr-1" />
              Sačuvaj izmene
            </b-button>
          </div>
        </b-form>

        <div v-else>
          <div class="row">
            <div class="col-md-6">
              <h6 class="text-muted">Pošiljka</h6>
              <p class="mb-1"><strong>ID:</strong> #{{ detailShipment.id }}</p>
              <p class="mb-1">
                <strong>CE ID:</strong> {{ detailShipment.shipment_id || "—" }}
              </p>
              <p class="mb-1">
                <strong>Status:</strong>
                <b-badge
                  :variant="badgeVariant(detailShipment.status)"
                  class="ml-1"
                >
                  {{ statusLabel(detailShipment.status) }}
                </b-badge>
              </p>
              <p class="mb-1">
                <strong>Kreirano:</strong>
                {{ formatDateTime(detailShipment.created_at) }}
              </p>
              <p class="mb-1" v-if="detailShipment.committed_at">
                <strong>Prva štampa:</strong>
                {{ formatDateTime(detailShipment.committed_at) }}
              </p>
              <p class="mb-1" v-if="detailShipment.printed_at">
                <strong>Štampano:</strong>
                {{ formatDateTime(detailShipment.printed_at) }}
              </p>
              <p class="mb-1" v-if="detailShipment.cancelled_at">
                <strong>Otkazano:</strong>
                {{ formatDateTime(detailShipment.cancelled_at) }}
              </p>
              <p class="mb-1">
                <strong>Referenca:</strong>
                {{ detailShipment.reference || "—" }}
              </p>
              <p class="mb-1">
                <strong>{{
                  detailShipment.is_pallet ? "Broj paleta:" : "Broj paketa:"
                }}</strong>
                {{ detailShipment.number_of_packages }}
              </p>
              <p class="mb-1" v-if="detailShipment.is_pallet">
                <strong>Paleta:</strong> Da
              </p>
              <p class="mb-1" v-if="detailShipment.payment_by === 'recipient'">
                <strong>Poštarinu plaća:</strong> Primalac
              </p>
              <p class="mb-1" v-if="detailShipment.pickup_requested_at">
                <strong>Kraj dana:</strong>
                {{ formatDateTime(detailShipment.pickup_requested_at) }}
              </p>
              <p class="mb-1">
                <strong>Osnovica (RSD):</strong>
                {{ formatTaxValue(detailShipment, "net_value") }}
              </p>
              <p class="mb-1">
                <strong>PDV (RSD):</strong>
                {{ formatTaxValue(detailShipment, "vat_value") }}
              </p>
              <p class="mb-1">
                <strong>Ukupno (RSD):</strong>
                {{ formatMoney(detailShipment.value) }}
              </p>
              <p class="mb-1" v-if="detailShipment.cod_amount">
                <strong>Otkup:</strong>
                {{ formatMoney(detailShipment.cod_amount) }}
              </p>
              <p class="mb-1" v-if="detailShipment.note">
                <strong>Napomena:</strong> {{ detailShipment.note }}
              </p>
              <p class="mb-1 text-danger" v-if="detailShipment.last_error">
                <strong>Greška:</strong> {{ detailShipment.last_error }}
              </p>
            </div>
            <div class="col-md-6">
              <h6 class="text-muted">Primalac</h6>
              <p class="mb-1">{{ detailShipment.cosignee_name }}</p>
              <p class="mb-1">
                {{ detailShipment.cosignee_street_and_number }}
              </p>
              <p class="mb-1">
                {{ detailShipment.cosignee_zipcode }}
                {{ detailShipment.cosignee_city }},
                {{ detailShipment.cosignee_country_code }}
              </p>
              <p class="mb-1" v-if="detailShipment.cosignee_contact_name">
                {{ detailShipment.cosignee_contact_name }}
              </p>
              <p class="mb-1" v-if="detailShipment.cosignee_phone">
                {{ detailShipment.cosignee_phone }}
              </p>
              <p class="mb-1" v-if="detailShipment.cosignee_email">
                {{ detailShipment.cosignee_email }}
              </p>

              <h6 class="text-muted mt-3">Otpremnice</h6>
              <ul class="list-unstyled mb-0">
                <li v-for="o in detailShipment.otpremnice || []" :key="o.id">
                  {{ o.wings_otpremnica_id }} —
                  {{ o.reference_snapshot || o.broj_profakture_snapshot }}
                  <small class="d-block text-muted mb-2">
                    Osnovica: {{ formatTaxValue(o, "net_value") }}; PDV:
                    {{ formatTaxValue(o, "vat_value") }}; Ukupno:
                    {{ formatMoney(o.vrednost_snapshot) }} RSD
                  </small>
                  <small
                    v-if="o.net_value_basis === 'document_total'"
                    class="d-block text-muted"
                  >
                    Uključene usluge iz profakture
                  </small>
                </li>
              </ul>
            </div>
          </div>

          <hr />

          <!-- Status timeline pulled from CE's /shipmentbyid. The list is
             stored alongside the shipment so it survives reloads; the
             button re-pulls it on demand for live tracking. -->
          <div class="d-flex align-items-center mb-2">
            <h6 class="text-muted mb-0">Istorija statusa</h6>
            <b-button
              v-if="detailShipment.shipment_id"
              size="sm"
              variant="outline-primary"
              class="ml-auto"
              :disabled="refreshingRowId === detailShipment.id"
              @click="refreshOneStatus(detailShipment)"
            >
              <b-spinner
                v-if="refreshingRowId === detailShipment.id"
                small
                class="mr-1"
              />
              Osveži iz CE
            </b-button>
          </div>
          <b-table
            v-if="
              detailShipment.status_events &&
                detailShipment.status_events.length
            "
            :items="detailShipment.status_events"
            :fields="statusEventFields"
            small
            striped
            responsive
            class="mb-0"
          >
            <template #cell(event_at)="row">
              {{ formatDateTime(row.item.event_at) || "—" }}
            </template>
            <template #cell(code)="row">
              <code v-if="row.item.code">{{ row.item.code }}</code>
              <span v-else class="text-muted">—</span>
            </template>
            <template #cell(message)="row">
              {{ row.item.message || "—" }}
            </template>
          </b-table>
          <div v-else class="small text-muted">
            Nema sačuvanih statusa za ovu pošiljku.
            {{
              detailShipment.shipment_id
                ? "Klikni „Osveži iz CE“ da povučeš trenutni."
                : "Pošiljka nema CE shipment id, ne može se osvežiti."
            }}
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<style scoped>
.ce-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}
.ce-toolbar__left {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.5rem;
  flex: 1 1 auto;
}
.ce-toolbar__right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-left: 0.75rem;
  border-left: 1px solid #e5e7eb;
}
.ce-search {
  min-width: 260px;
  max-width: 360px;
  flex: 1 1 260px;
}
.ce-dates {
  width: auto;
  flex: 0 0 auto;
}
.ce-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.ce-chip {
  font-weight: 400;
  font-size: 0.8rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid #d1d5db;
  cursor: pointer;
  user-select: none;
}
.ce-chip:hover {
  background: #f3f4f6;
}
.ce-chip__x {
  margin-left: 0.25rem;
  color: #6b7280;
  font-weight: 600;
}
@media (max-width: 768px) {
  .ce-toolbar__right {
    border-left: none;
    padding-left: 0;
    width: 100%;
  }
}
</style>
